import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ConciergeCTA = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Helper function to format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, ''); // remove all non-digits
    const phoneNumberLength = phoneNumber.length;
    
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ 
          "form-name": "demo-requests", 
          ...formData 
        }),
      });

      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Demo request submitted! We\'ll be in touch within 24 hours.');
    } catch (error) {
      setIsSubmitting(false);
      toast.error('There was an error submitting your request. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Define character limits for each field
    const limits = {
      name: 50,
      email: 100,
      company: 100,
      phone: 14 // Limits based on (XXX) XXX-XXXX format
    };

    // Enforce limit by truncating value
    const truncatedValue = value.slice(0, limits[name]);
    
    // Apply phone formatting if the input is the phone field
    const updatedValue = name === 'phone' ? formatPhoneNumber(truncatedValue) : truncatedValue;

    setFormData(prev => ({
      ...prev,
      [name]: updatedValue
    }));
  };

  return (
    <section id="concierge" className="py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-[#000000]" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00FFD1]/5 rounded-full blur-[150px]" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-[7.6923%]">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div>
              <span className="text-[#00FFD1] text-sm font-medium tracking-wider uppercase">White Glove Onboarding</span>
              <h2 className="text-4xl md:text-5xl font-semibold text-white mt-4 leading-tight">
                We Set It Up.
                <span className="block text-white/50">You Just Drive.</span>
              </h2>
            </div>
            
            <p className="text-white/60 text-xl leading-relaxed">
              No setup required on your end. Our concierge team configures your database, 
              imports your products, and sets your routes. You just log in and start working.
            </p>
            
            <div className="space-y-4">
              {[
                'Live Demo with your specific use case',
                'Customized route and store data setup',
                'Full product catalog synchronization',
                'Team-wide training and onboarding',
                'Ongoing technical support & optimization'
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#00FFD1] flex-shrink-0" />
                  <span className="text-white/80">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-[#121212] border border-white/10 p-8 lg:p-10">
            {isSubmitted ? (
              <div className="text-center py-12 space-y-4">
                <div className="w-16 h-16 bg-[#00FFD1]/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8 text-[#00FFD1]" />
                </div>
                <h3 className="text-2xl font-semibold text-white">Request Received!</h3>
                <p className="text-white/60">Our team will reach out within 24 hours to schedule your personalized demo.</p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-semibold text-white mb-2">Request a Demo</h3>
                <p className="text-white/50 mb-8">See Ruma Logistics in action with a personalized walkthrough.</p>
                
                <form 
                  name="demo-requests"
                  onSubmit={handleSubmit} 
                  className="space-y-5"
                  data-netlify="true"
                >
                  <input type="hidden" name="form-name" value="demo-requests" />
                  
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      maxLength={50}
                      className="w-full bg-[#000000] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#00FFD1]/50 transition-colors"
                      placeholder="John Smith"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      className="w-full bg-[#000000] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#00FFD1]/50 transition-colors"
                      placeholder="john@company.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Company Name</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      maxLength={100}
                      className="w-full bg-[#000000] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#00FFD1]/50 transition-colors"
                      placeholder="ABC Distribution"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      maxLength={14}
                      className="w-full bg-[#000000] border border-white/10 px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#00FFD1]/50 transition-colors"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#00FFD1] text-black px-6 py-4 font-medium text-lg flex items-center justify-center gap-3 transition-all duration-400 hover:bg-[#00FFD1]/10 hover:text-[#00FFD1] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Request Demo
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConciergeCTA;